import { Injectable } from '@angular/core';
import { Observable, forkJoin, Subject } from 'rxjs';
import { BuildingContact } from '../model/building-contact';
import { ApiRouteConfig } from '../../../config/api.route.config';
import {HttpClient, HttpParams} from '@angular/common/http';
import { BuildingContactFunction } from '../model/building-contact-function';
import { AppConfig } from '../../../config/app.config';
import { AppDocument } from '../model/app-document';
import * as FileSaver from '../../../../../node_modules/file-saver/FileSaver.min.js';
import {of} from 'rxjs';
import {Contact} from '../model/contact';
import {PaginationResponse} from '../model/pagination-response';
import { map, catchError, tap } from 'rxjs/operators';
import {StructureContactType} from "../enums/enums";
import { BuildingContactLocation } from '../model/building-contact-location';

@Injectable()
export class ContactsService {

  observable: Observable<PaginationResponse<BuildingContact>>;
  contacts: PaginationResponse<BuildingContact>;
  detailsUpdated$ = new Subject<any>();
  functionsUpdated$ = new Subject<any>();
  deleteSubject$ = new Subject<number>();
  added$ = new Subject<BuildingContact>();

  constructor(private httpClient: HttpClient, private config: ApiRouteConfig, private appConfig: AppConfig) {}

  public getContactFullNameByLogin(): Observable<string> {
    return this.httpClient.get<string>(this.config.CONTACT_FULLNAME_BY_LOGIN);
  }

  public addToFavourites(contactId: string): Observable<boolean> {
    return this.httpClient.post<boolean>(
      this.config.getRouteWithInjectedParams(this.config.CONTACT_FAVOURITE, [contactId]), {IsFavourite: true});
  }

  public removeFromFavourites(contactId: string): Observable<boolean> {
    return this.httpClient.post<boolean>(
      this.config.getRouteWithInjectedParams(this.config.CONTACT_FAVOURITE, [contactId]), {IsFavourite: false});
  }

  public downloadGDPRPdfFile(contactId: string) {
    return this.httpClient.post(
      this.config.getRouteWithInjectedParams(this.config.CONTACT_GDPR_REPORT, [contactId]), {} ,{observe: 'response', responseType: 'arraybuffer'})
      .pipe(map((response) => {
          let blob = new Blob([response.body], {type: response.headers.get('content-type')});
          FileSaver.saveAs(blob, response.headers.get('filename'));
          return true;
        },
        err => {
          return false;
        }));
  }

  getContacts(includeBuildingLoginContacts: boolean = true): Observable<PaginationResponse<BuildingContact>> {
    if (this.contacts) {
      return of(this.contacts);
    } else {
      if (this.observable == null) {
        const params = new HttpParams()
            .append('includeBuildingLoginContacts', includeBuildingLoginContacts.toString());
        this.observable = this.getContactsFromServer(1, null, params);
        this.observable.pipe(tap(contacts => this.contacts = contacts));
      }
      return this.observable;
    }
  }

  getContactsOfType(...Functions:StructureContactType[]) {
    let params = new HttpParams()
      .append('includeBuildingLoginContacts', 'true')
      .appendAll({Functions})
    return this.getContactsFromServer(1, null, params);
  }

  public getContactsFromServer(pageNumber: number, pageSize: number, params: HttpParams): Observable<PaginationResponse<BuildingContact>> {
    params = params.append('pageNumber', pageNumber.toString());

    if(pageSize) {
      params = params.append('pageSize', pageSize.toString());
    }

    return this.httpClient.get<PaginationResponse<BuildingContact>>(this.config.CONTACTS, {params: params})
      .pipe(
        map(response => {
          response.content.map(contact => {
            if (contact.photoUrl == null) {
              contact.photoUrl = this.getDefaultAvatarUrl();
            }
          });
          return response;
        }),
        catchError(e => {
          return of({page: 1, total: 0, content: []} as PaginationResponse<BuildingContact>);
        })
      );
  }

  private getDefaultAvatarUrl(): string {
    return this.appConfig.DEFAULT_AVATAR_URL;
  }

  public createContact(contact: Contact): Observable<BuildingContact> {
    this.setContactOrganizationToNullIfItsEmptyString(contact);
    return this.httpClient.put<BuildingContact>(this.config.CONTACTS, contact).pipe(
      tap((c) => {
        this.contacts = null;
        this.added$.next(c);
      }),
      catchError(error => {
        this.contacts = null;
        return of({} as BuildingContact);
      })
    );
  }

  public getContact(id: number): Observable<BuildingContact> {
    return forkJoin([
      this.httpClient.get<BuildingContact>(
        this.config.getRouteWithInjectedParams(this.config.CONTACT, [
          id.toString()
        ])
      ),
      this.httpClient.get<boolean>(
        this.config.getRouteWithInjectedParams(
          this.config.CONTACT_IS_RESIDENT,
          [id.toString()]
        )
      )
    ]).pipe(
      map(([contact, isResident]) => {
        if (contact.photoUrl == null) {
          contact.photoUrl = this.getDefaultAvatarUrl();
        }
        return {...contact, isResident};
      })
    );
  }

  updateContact(value: BuildingContact): Observable<boolean> {
    const contact: BuildingContact = Object.assign({}, value);
    return this.httpClient.post<boolean>(this.config.CONTACTS, contact)
      .pipe(
        tap(() => this.detailsUpdated$.next(null))
      );
  }

  deleteContact(contactId: number): Observable<boolean> {
    return this.httpClient.delete<boolean>(this.config.getRouteWithInjectedParams(this.config.CONTACT, [contactId.toString()]))
      .pipe(
        tap(() => {
          this.contacts = null;
          this.deleteSubject$.next(contactId);
        })
      );
  }

  getFunctions(contactId: number): Observable<BuildingContactFunction[]> {
    return this.httpClient.get<BuildingContactFunction[]>(
      this.config.getRouteWithInjectedParams(this.config.CONTACT_FUNCTIONS, [contactId.toString()]));
  }

  updateFunction(contactId: number, f: BuildingContactFunction): Observable<boolean> {
    const url = this.config.getRouteWithInjectedParams(this.config.CONTACT_FUNCTIONS, [contactId.toString()]);
    return this.httpClient.post<boolean>(url, f)
      .pipe(
        tap(() => this.functionsUpdated$.next(null))
      );
  }

  addFunction(contactId: number, f: BuildingContactFunction): Observable<BuildingContactFunction> {
    const url = this.config.getRouteWithInjectedParams(this.config.CONTACT_FUNCTIONS, [contactId.toString()]);
    return this.httpClient.put<BuildingContactFunction>(url, f)
      .pipe(
        tap(() => this.functionsUpdated$.next(null))
      );
  }

  getLocations(contactId: number): Observable<BuildingContactLocation[]> {
    return this.httpClient.get<BuildingContactLocation[]>(
      this.config.getRouteWithInjectedParams(this.config.CONTACT_LOCATIONS, [contactId.toString()]));
  }

  getDocuments(contactId: number): Observable<AppDocument[]> {
    return this.httpClient.get<AppDocument[]>(this.config.getRouteWithInjectedParams(
      this.config.CONTACT_DOCUMENTS, [contactId.toString()]));
  }

  updatePhoto(contact: BuildingContact, photo: File): Observable<string> {
    const formData: FormData = new FormData();
    formData.append('avatar', photo, photo.name);
    return this.httpClient.post<string>(
      this.config.getRouteWithInjectedParams(this.config.CONTACT_AVATAR, [contact.id.toString()]
      ),
      formData).pipe(tap(x => {
        contact.photoUrl = this.getDefaultAvatarUrl();
        this.detailsUpdated$.next(contact);
      }));
  }

  deleteFunction(contactId: number, contactFunctionId: number): Observable<boolean> {
    return this.httpClient.delete<boolean>(this.config.getRouteWithInjectedParams(this.config.CONTACT_FUNCTIONS_DELETE,
      [contactId.toString(), contactFunctionId.toString()]));
  }

  addDocument(contactId: number, file: File, document: AppDocument): Observable<AppDocument> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('managerDocumentRequest.label', document.label);
    formData.append('managerDocumentRequest.description', document.description ? document.description : '');

    return this.httpClient.put<AppDocument>(this.config.getRouteWithInjectedParams(this.config.CONTACT_DOCUMENTS_FILE, [contactId.toString()]),
      formData);
  }

  updateDocument(contactId: number, document: AppDocument, file: File): Observable<any> {
    const formData = new FormData();
    if (file) {
      formData.append('file', file, file.name);
    }
    formData.append('managerDocumentRequest.id', document.id.toString());
    formData.append('managerDocumentRequest.label', document.label);
    formData.append('managerDocumentRequest.description', document.description ? document.description : '');

    return this.httpClient.put<any>(this.config.getRouteWithInjectedParams(this.config.CONTACT_DOCUMENTS,
      [contactId.toString()]), formData);
  }

  deleteDocument(contactId: number, documentId: number): Observable<boolean> {
    return this.httpClient.delete<boolean>(this.config.getRouteWithInjectedParams(this.config.CONTACT_DOCUMENT_DELETE,
      [contactId.toString(), documentId.toString()]));
  }

  private setContactOrganizationToNullIfItsEmptyString(contact: Contact) {
    if (contact.company === '') {
      contact.company = null;
    }
  }
}
